import { useEffect, useMemo } from 'react';
import '../@mock-api';
import moment from 'moment';
import 'moment/locale/es-mx';
import { useSelector } from 'react-redux';
import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { SnackbarProvider } from 'notistack';
import { ReactKeycloakProvider,useKeycloak } from '@react-keycloak/web';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import BrowserRouter from '../@fuse/core/BrowserRouter';
import FuseLayout from '../@fuse/core/FuseLayout';
import FuseTheme from '../@fuse/core/FuseTheme';
import { selectCurrentLanguageDirection } from './store/i18nSlice';
import themeLayouts from './theme-layouts/themeLayouts';
import { selectMainTheme, selectMainThemeDark } from './store/fuse/settingsSlice';
import withAppProviders from './withAppProviders';
import {keycloak} from '../keycloack';

const emotionCacheOptions = {
  rtl: {
    key: 'muirtl',
    stylisPlugins: [rtlPlugin],
    insertionPoint: document.getElementById('emotion-insertion-point'),
  },
  ltr: {
    key: 'muiltr',
    stylisPlugins: [],
    insertionPoint: document.getElementById('emotion-insertion-point'),
  },
};

const AppContent = () => {
  const darkMode = useSelector((state) => state?.fuse?.dashboard?.darkMode);
  const langDirection = useSelector(selectCurrentLanguageDirection);
  const mainTheme = useSelector(selectMainTheme);
  const mainDarkTheme = useSelector(selectMainThemeDark);
  const { keycloak } = useKeycloak();

  useEffect(() => {
    moment.locale('es-mx');
  }, []);

  const emotionCache = useMemo(() => createCache(emotionCacheOptions[langDirection]), [langDirection]);
  const theme = useMemo(() => darkMode ? mainDarkTheme : mainTheme, [darkMode, mainDarkTheme, mainTheme]);

  if (!keycloak.authenticated) {
    return null; 
  }

  return (
    <CacheProvider value={emotionCache}>
      <FuseTheme theme={theme} direction={langDirection}>
        <BrowserRouter>
          <SnackbarProvider
            maxSnack={5}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            classes={{
              containerRoot: 'bottom-0 right-0 mb-52 md:mb-68 mr-8 lg:mr-80 z-99',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FuseLayout layouts={themeLayouts} />
            </LocalizationProvider>
          </SnackbarProvider>
        </BrowserRouter>
      </FuseTheme>
    </CacheProvider>
  );
};

const AppWithProviders = withAppProviders(AppContent);

const App = () => (
  <ReactKeycloakProvider
    authClient={keycloak}
    initOptions={{ onLoad: 'login-required', checkLoginIframe: false }}
  >
    <AppWithProviders />
  </ReactKeycloakProvider>
);

export default App;